import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Eye, EyeOff, Lock, Mail, ShieldCheck, Sparkles, UserRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

function extractMessage(error) {
  const code = String(error?.code || '');
  if (code.includes('email-already-in-use')) {
    return 'This email is already registered.';
  }
  if (code.includes('invalid-email')) {
    return 'Please enter a valid email address.';
  }
  if (code.includes('weak-password')) {
    return 'Password must be at least 6 characters.';
  }
  if (code.includes('popup-closed-by-user')) {
    return 'Signup popup was closed.';
  }
  return error?.message || 'Unable to create account right now.';
}

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signupWithEmail, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSignup = async (event) => {
    event.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Password and confirm password do not match.');
      return;
    }

    setLoading(true);
    try {
      await signupWithEmail(name, email, password);
      navigate('/profile');
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setLoading(true);
    setError('');

    try {
      await loginWithGoogle();
      navigate('/profile');
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="auth-page">
      <article className="auth-shell">
        <span className="auth-orb auth-orb-one" aria-hidden="true" />
        <span className="auth-orb auth-orb-two" aria-hidden="true" />

        <div className="auth-promo">
          <p className="auth-kicker">Create Your Account</p>
          <h1>Join the Ditoo-Pro creator community.</h1>
          <p>Set up your account to unlock quick checkout and order tracking from one dashboard.</p>

          <ul className="auth-benefit-list">
            <li>
              <ShieldCheck size={16} />
              Verified account identity with secure Firebase auth
            </li>
            <li>
              <Sparkles size={16} />
              Save your profile for faster purchases next time
            </li>
            <li>
              <UserRound size={16} />
              Build your personalized pixel-tech shopping experience
            </li>
          </ul>

          <div className="auth-highlight">
            <strong>6-Month Seller Warranty</strong>
            <p>Manufacturing defect support with dedicated customer assistance.</p>
          </div>
        </div>

        <div className="auth-card">
          <h2>Create Account</h2>
          <p>Continue with Google or register using your email.</p>

          {error ? <p className="auth-error">{error}</p> : null}

          <button type="button" className="auth-google-btn" onClick={handleGoogleSignup} disabled={loading}>
            Continue with Google <ArrowRight size={16} />
          </button>

          <div className="auth-divider">
            <span>or sign up with email</span>
          </div>

          <form className="auth-form" onSubmit={handleSignup}>
            <label htmlFor="name">Full Name</label>
            <div className="auth-input-wrap">
              <UserRound size={16} />
              <input
                id="name"
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                required
              />
            </div>

            <label htmlFor="email">Email</label>
            <div className="auth-input-wrap">
              <Mail size={16} />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <label htmlFor="password">Password</label>
            <div className="auth-input-wrap">
              <Lock size={16} />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
              />
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <label htmlFor="confirm">Confirm Password</label>
            <div className="auth-input-wrap">
              <Lock size={16} />
              <input
                id="confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
              />
              <button
                type="button"
                className="auth-eye-btn"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            <p className="auth-help">Use at least 6 characters for a stronger password.</p>

            <button type="submit" className="solid-btn auth-submit-btn" disabled={loading}>
              {loading ? 'Creating account...' : 'Create account with Email'}
            </button>
          </form>

          <p className="auth-switch">
            Already have an account? <Link to="/login">Login</Link>
          </p>
        </div>
      </article>
    </section>
  );
}
